/**
 * Report Builder Utilities
 * 
 * Shared utility functions for managing report builder elements (charts and text blocks)
 */

(function () {
  "use strict";

  /**
   * Preserve and restore TinyMCE editors when DOM elements are moved
   */
  function preserveTinyMCEEditors(container) {
    const editors = [];
    
    // Find all textareas that might have TinyMCE editors
    const textareas = container.querySelectorAll('textarea[id^="wysiwyg_"]');
    
    textareas.forEach((textarea) => {
      const editorId = textarea.id;
      let content = textarea.value || "";
      let isHidden = false;
      
      // Try to get content from TinyMCE if it exists
      if (typeof tinymce !== "undefined" && tinymce.get(editorId)) {
        const editor = tinymce.get(editorId);
        try {
          // Save editor content first
          editor.save();
          content = editor.getContent();
          isHidden = editor.isHidden();
        } catch (e) {
          // Fallback to textarea value if editor save fails
          content = textarea.value || "";
        }
      }
      
      editors.push({
        id: editorId,
        content: content,
        isHidden: isHidden
      });
    });
    
    return editors;
  }

  /**
   * Restore TinyMCE editors after DOM manipulation
   */
  function restoreTinyMCEEditors(editors) {
    if (!editors || editors.length === 0) {
      return;
    }

    // Remove all existing editors first
    editors.forEach((editorData) => {
      if (typeof wp !== "undefined" && wp.editor && wp.editor.remove) {
        try {
          wp.editor.remove(editorData.id);
        } catch (e) {
          // Editor might not exist, that's okay
        }
      }
    });

    // Wait a bit before reinitializing
    setTimeout(function() {
      editors.forEach((editorData) => {
        const textarea = document.getElementById(editorData.id);
        if (!textarea) {
          return;
        }

        // Set content
        textarea.value = editorData.content;

        // Reinitialize editor
        if (typeof wp !== "undefined" && wp.editor && wp.editor.initialize) {
          const editorSettings = {
            tinymce: {
              toolbar1: "formatselect,bold,italic,bullist,numlist,blockquote,alignleft,aligncenter,alignright,link,unlink",
              toolbar2: "",
              height: 300,
              wpautop: true,
              block_formats: "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6;",
              setup: function(editor) {
                editor.on('change', function() {
                  editor.save();
                });
              }
            },
            quicktags: true,
            mediaButtons: false,
          };

          wp.editor.initialize(editorData.id, editorSettings);
          
          // Restore hidden state if needed
          if (editorData.isHidden && typeof tinymce !== "undefined") {
            setTimeout(function() {
              const editor = tinymce.get(editorData.id);
              if (editor && !editor.isHidden()) {
                editor.hide();
              }
            }, 200);
          }
        }
      });
    }, 100);
  }

  /**
   * Move element up in order (works with mixed element types)
   * @param {HTMLElement} elementContainer - The container element to move
   * @param {string} itemId - The ID of the item
   * @param {string} idAttribute - The attribute name for the ID (e.g., 'data-chart-id' or 'data-text-block-id')
   */
  function moveElementUp(elementContainer, itemId, idAttribute) {
    // Handle type detection for idAttribute
    if (!idAttribute) {
      // Try to detect type from container
      if (elementContainer.classList.contains('canvas-faq-item')) {
        idAttribute = 'data-faq-id';
      } else if (elementContainer.classList.contains('canvas-chart-item')) {
        idAttribute = 'data-chart-id';
      } else if (elementContainer.classList.contains('canvas-text-item')) {
        idAttribute = 'data-text-block-id';
      } else if (elementContainer.classList.contains('canvas-image-item')) {
        idAttribute = 'data-image-id';
      } else if (elementContainer.classList.contains('canvas-calculator-item')) {
        idAttribute = 'data-calculator-id';
      } else if (elementContainer.classList.contains('canvas-patient-story-item')) {
        idAttribute = 'data-patient-story-id';
      }
    }
    const canvasCharts = document.getElementById("canvasCharts");
    if (!canvasCharts || !elementContainer) {
      return false;
    }

    const previousContainer = elementContainer.previousElementSibling;
    if (!previousContainer) {
      return false; // Already at top
    }

    // Preserve ALL TinyMCE editors before moving (from entire canvas)
    const allContainers = Array.from(canvasCharts.children);
    const editors = [];
    allContainers.forEach((container) => {
      const preserved = preserveTinyMCEEditors(container);
      editors.push(...preserved);
    });

    // Move in DOM
    canvasCharts.insertBefore(elementContainer, previousContainer);

    // Restore TinyMCE editors after moving
    setTimeout(() => {
      restoreTinyMCEEditors(editors);
    }, 50);

    // Scroll to new position
    setTimeout(() => {
      elementContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);

    return true;
  }

  /**
   * Move element down in order (works with mixed element types)
   * @param {HTMLElement} elementContainer - The container element to move
   * @param {string} itemId - The ID of the item
   * @param {string} idAttribute - The attribute name for the ID
   */
  function moveElementDown(elementContainer, itemId, idAttribute) {
    // Handle type detection for idAttribute
    if (!idAttribute) {
      // Try to detect type from container
      if (elementContainer.classList.contains('canvas-faq-item')) {
        idAttribute = 'data-faq-id';
      } else if (elementContainer.classList.contains('canvas-chart-item')) {
        idAttribute = 'data-chart-id';
      } else if (elementContainer.classList.contains('canvas-text-item')) {
        idAttribute = 'data-text-block-id';
      } else if (elementContainer.classList.contains('canvas-image-item')) {
        idAttribute = 'data-image-id';
      } else if (elementContainer.classList.contains('canvas-calculator-item')) {
        idAttribute = 'data-calculator-id';
      } else if (elementContainer.classList.contains('canvas-patient-story-item')) {
        idAttribute = 'data-patient-story-id';
      }
    }
    const canvasCharts = document.getElementById("canvasCharts");
    if (!canvasCharts || !elementContainer) {
      return false;
    }

    const nextContainer = elementContainer.nextElementSibling;
    if (!nextContainer) {
      return false; // Already at bottom
    }

    // Preserve ALL TinyMCE editors before moving (from entire canvas)
    const allContainers = Array.from(canvasCharts.children);
    const editors = [];
    allContainers.forEach((container) => {
      const preserved = preserveTinyMCEEditors(container);
      editors.push(...preserved);
    });

    // Move in DOM
    canvasCharts.insertBefore(nextContainer, elementContainer);

    // Restore TinyMCE editors after moving
    setTimeout(() => {
      restoreTinyMCEEditors(editors);
    }, 50);

    // Scroll to new position
    setTimeout(() => {
      elementContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);

    return true;
  }

  /**
   * Make title editable
   * @param {string} itemId - Chart or text block ID
   * @param {string} type - "chart" or "text"
   * @param {Function} updateCallback - Callback to update the config with new title
   */
  function makeTitleEditable(itemId, type, updateCallback) {
    let titleSelector;
    if (type === "chart") {
      titleSelector = `.chart-item-title .title-text[data-chart-id="${itemId}"]`;
    } else if (type === "text") {
      titleSelector = `.text-item-title .title-text[data-text-block-id="${itemId}"]`;
    } else if (type === "faq") {
      titleSelector = `.faq-item-title .title-text[data-faq-id="${itemId}"]`;
    } else if (type === "image") {
      titleSelector = `.image-item-title .title-text[data-image-id="${itemId}"]`;
    } else if (type === "calculator") {
      titleSelector = `.calculator-item-title .title-text[data-calculator-id="${itemId}"]`;
    } else if (type === "patient-story") {
      titleSelector = `.patient-story-item-title .title-text[data-patient-story-id="${itemId}"]`;
    } else {
      return;
    }
    
    const titleElement = document.querySelector(titleSelector);
    if (!titleElement) {
      return;
    }

    // Check if already in edit mode
    if (titleElement.contentEditable === "true") {
      return;
    }

    const currentTitle = titleElement.textContent.trim();
    const originalTitle = titleElement.textContent;

    // Make editable
    titleElement.contentEditable = "true";
    titleElement.style.cursor = "text";
    titleElement.style.borderBottom = "1px dashed #1e3356";
    titleElement.style.paddingBottom = "2px";
    titleElement.focus();

    // Select all text for easy editing
    const range = document.createRange();
    range.selectNodeContents(titleElement);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    // Handle save on blur or Enter key
    const saveTitle = function() {
      const newTitle = titleElement.textContent.trim();
      
      // Allow empty titles - update config regardless
      if (updateCallback) {
        updateCallback(newTitle);
      }
      
      // If empty, show a placeholder or keep it empty
      if (newTitle === "") {
        titleElement.textContent = ""; // Keep empty
      }

      // Remove edit mode
      titleElement.contentEditable = "false";
      titleElement.style.cursor = "default";
      titleElement.style.borderBottom = "";
      titleElement.style.paddingBottom = "";
    };

    // Save on blur
    titleElement.addEventListener("blur", saveTitle, { once: true });

    // Save on Enter key
    titleElement.addEventListener("keydown", function(e) {
      if (e.key === "Enter") {
        e.preventDefault();
        titleElement.blur();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        titleElement.textContent = originalTitle;
        titleElement.blur();
      }
    });
  }

  /**
   * Escape HTML to prevent XSS attacks
   * @param {string} text - Text to escape
   * @returns {string} Escaped HTML
   */
  function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Get canvas container element
   * @returns {HTMLElement|null} Canvas container or null if not found
   */
  function getCanvasContainer() {
    const canvasCharts = document.getElementById("canvasCharts");
    if (!canvasCharts) {
      console.error("Canvas charts container not found");
    }
    return canvasCharts;
  }

  /**
   * Show or hide empty message based on whether canvas has items
   * @param {boolean} show - Whether to show the empty message
   */
  function toggleEmptyMessage(show) {
    const emptyMessage = document.querySelector(".canvas-empty-message");
    if (emptyMessage) {
      emptyMessage.style.display = show ? "block" : "none";
    }
  }

  /**
   * Update array order to match DOM order
   * @param {Array} itemsArray - Array of item configs
   * @param {string} itemId - ID of the item to update
   * @param {string} containerIdPrefix - Prefix for container ID (e.g., "chart_container_", "faq_container_")
   * @param {string} idAttribute - Data attribute name (e.g., "data-chart-id", "data-faq-id")
   * @returns {boolean} True if order was updated, false otherwise
   */
  function updateArrayOrder(itemsArray, itemId, containerIdPrefix, idAttribute) {
    const canvasCharts = getCanvasContainer();
    if (!canvasCharts) {
      return false;
    }

    const itemIndex = itemsArray.findIndex((item) => item.id === itemId);
    if (itemIndex < 0) {
      return false;
    }

    const allItems = Array.from(canvasCharts.children);
    const itemContainer = document.getElementById(`${containerIdPrefix}${itemId}`);
    if (!itemContainer) {
      return false;
    }

    const newIndex = allItems.indexOf(itemContainer);
    if (newIndex >= 0) {
      const item = itemsArray[itemIndex];
      itemsArray.splice(itemIndex, 1);
      // Find position in array based on DOM order
      let insertIndex = 0;
      for (let i = 0; i < newIndex; i++) {
        const itemIdAttr = allItems[i].getAttribute(idAttribute);
        if (itemIdAttr) {
          const idx = itemsArray.findIndex((item) => item.id === itemIdAttr);
          if (idx >= 0) {
            insertIndex = idx + 1;
          }
        }
      }
      itemsArray.splice(insertIndex, 0, item);
      return true;
    }
    return false;
  }

  /**
   * Setup move up and move down buttons for an item
   * @param {HTMLElement} container - Item container element
   * @param {string} itemId - Item ID
   * @param {string} idAttribute - Data attribute name (e.g., "data-chart-id")
   * @param {Function} onOrderChange - Callback when order changes
   */
  function setupMoveButtons(container, itemId, idAttribute, onOrderChange) {
    // Setup move up button
    const moveUpBtn = container.querySelector(".btn-move-up");
    if (moveUpBtn) {
      moveUpBtn.addEventListener("click", function () {
        if (window.ReportBuilderUtils && window.ReportBuilderUtils.moveElementUp) {
          const moved = window.ReportBuilderUtils.moveElementUp(
            container,
            itemId,
            idAttribute
          );
          if (moved && onOrderChange) {
            onOrderChange();
          }
        }
      });
    }

    // Setup move down button
    const moveDownBtn = container.querySelector(".btn-move-down");
    if (moveDownBtn) {
      moveDownBtn.addEventListener("click", function () {
        if (window.ReportBuilderUtils && window.ReportBuilderUtils.moveElementDown) {
          const moved = window.ReportBuilderUtils.moveElementDown(
            container,
            itemId,
            idAttribute
          );
          if (moved && onOrderChange) {
            onOrderChange();
          }
        }
      });
    }
  }

  /**
   * Setup edit title button for an item
   * @param {HTMLElement} container - Item container element
   * @param {string} itemId - Item ID
   * @param {string} type - Item type ("chart", "faq", "text")
   * @param {Function} updateCallback - Callback to update the config with new title
   */
  function setupEditTitleButton(container, itemId, type, updateCallback) {
    const editTitleBtn = container.querySelector(".btn-edit-title");
    if (editTitleBtn) {
      editTitleBtn.addEventListener("click", function () {
        if (window.ReportBuilderUtils && window.ReportBuilderUtils.makeTitleEditable) {
          window.ReportBuilderUtils.makeTitleEditable(
            itemId,
            type,
            updateCallback
          );
        }
      });
    }
  }

  /**
   * Make report header element editable
   * @param {HTMLElement} element - Element to make editable
   * @param {Function} updateCallback - Optional callback to update the config with new content
   * @param {string} defaultText - Default text to show when content is empty
   */
  function makeReportHeaderEditable(element, updateCallback, defaultText) {
    if (!element) {
      return;
    }

    // Check if already in edit mode
    if (element.contentEditable === "true") {
      return;
    }

    // Make editable on click
    element.addEventListener("click", function handleClick() {
      if (element.contentEditable === "true") {
        return; // Already editing
      }

      const contentBeforeEdit = element.textContent;

      // Make editable
      element.contentEditable = "true";
      element.focus();

      // Select all text for easy editing
      const range = document.createRange();
      range.selectNodeContents(element);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      // Handle save on blur or Enter key
      const saveContent = function() {
        const newContent = element.textContent.trim();
        
        // Allow empty content - update config regardless
        if (updateCallback) {
          updateCallback(newContent);
        }
        
        // If empty, keep it empty (no default text restoration)
        if (newContent === "") {
          element.textContent = "";
        }

        // Remove edit mode
        element.contentEditable = "false";
      };

      // Save on Enter, cancel on Escape. Kept until editing ends (not just the
      // first key) so Enter works after typing.
      const handleKeydown = function(e) {
        if (e.key === "Enter") {
          e.preventDefault();
          element.blur();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          element.textContent = contentBeforeEdit;
          element.blur();
        }
      };
      element.addEventListener("keydown", handleKeydown);

      // Save on blur
      element.addEventListener("blur", function() {
        element.removeEventListener("keydown", handleKeydown);
        saveContent();
      }, { once: true });
    });
  }

  /**
   * Format date to display format (e.g., "Dec 18, 2025")
   * @param {string} dateString - Date string in YYYY-MM-DD format
   * @returns {string} Formatted date string
   */
  function formatDateDisplay(dateString) {
    if (!dateString) {
      return "";
    }

    const date = new Date(dateString + "T00:00:00"); // Add time to avoid timezone issues
    if (isNaN(date.getTime())) {
      return dateString; // Return original if invalid
    }

    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  }

  /**
   * Initialize date picker for report date
   */
  function initReportDatePicker() {
    const dateContainer = document.querySelector(".canvas-report-info-date");
    if (!dateContainer) {
      return;
    }

    const dateInput = dateContainer.querySelector(".canvas-report-date-input");
    const dateDisplay = dateContainer.querySelector(".canvas-report-date-display");

    if (!dateInput || !dateDisplay) {
      return;
    }

    // Update display when date input changes
    dateInput.addEventListener("change", function() {
      const formattedDate = formatDateDisplay(this.value);
      if (formattedDate) {
        dateDisplay.textContent = formattedDate;
        dateInput.style.display = "none";
        dateDisplay.style.display = "block";
      }
    });

    // Show date picker when display is clicked
    dateDisplay.addEventListener("click", function(e) {
      e.preventDefault();
      dateDisplay.style.display = "none";
      dateInput.style.display = "block";
      dateInput.focus();
      
      // Show native date picker if supported (modern browsers)
      if (typeof dateInput.showPicker === "function") {
        try {
          dateInput.showPicker();
        } catch (err) {
          // Fallback if showPicker fails (e.g., not triggered by user gesture)
          dateInput.click();
        }
      } else {
        // Fallback for older browsers
        dateInput.click();
      }
    });

    // Hide date picker if clicking outside
    dateInput.addEventListener("blur", function() {
      // Small delay to allow date selection
      setTimeout(function() {
        // Only hide if input is still not focused (date was selected)
        if (document.activeElement !== dateInput) {
          dateInput.style.display = "none";
          dateDisplay.style.display = "block";
        }
      }, 200);
    });

    // Also handle when date picker closes (for browsers that support it)
    dateInput.addEventListener("cancel", function() {
      dateInput.style.display = "none";
      dateDisplay.style.display = "block";
    });
  }

  /**
   * Initialize report header editing functionality
   */
  function initReportHeaderEditing() {
    const reportInfoContent = document.querySelector(".canvas-report-info-content");
    if (reportInfoContent) {
      // Make report name editable
      const reportName = reportInfoContent.querySelector("h4");
      if (reportName) {
        makeReportHeaderEditable(reportName, null, "Report Name");
      }
    }

    // Make report description editable (now outside of canvas-report-info-content)
    const reportDescription = document.querySelector(".canvas-report-description");
    if (reportDescription) {
      makeReportHeaderEditable(reportDescription, null, "Report Description");
    }

    // Initialize date picker instead of making it contentEditable
    initReportDatePicker();
  }

  /**
   * Show confirmation modal
   * @param {Object} options - Configuration options
   * @param {string} options.title - Modal title
   * @param {string} options.message - Modal message
   * @param {string} [options.confirmText] - Confirm button text (default: "Confirm")
   * @param {string} [options.cancelText] - Cancel button text (default: "Cancel")
   * @param {Function} options.onConfirm - Callback when confirmed
   * @param {Function} [options.onCancel] - Optional callback when cancelled
   */
  function showConfirmationModal(options) {
    const {
      title = "Confirm Action",
      message = "Are you sure you want to proceed?",
      confirmText = "Confirm",
      cancelText = "Cancel",
      onConfirm,
      onCancel,
    } = options;

    // Get or create modal
    let modal = document.getElementById("confirmationModal");
    if (!modal) {
      console.error("Confirmation modal not found in DOM");
      return;
    }

    // Update modal content
    const titleElement = modal.querySelector(".confirmation-modal-title");
    const messageElement = modal.querySelector(".confirmation-modal-message");
    const confirmBtn = modal.querySelector(".btn-modal-confirm");
    const cancelBtn = modal.querySelector(".btn-modal-cancel");

    if (titleElement) {
      titleElement.textContent = title;
    }
    if (messageElement) {
      messageElement.textContent = message;
    }
    if (confirmBtn) {
      confirmBtn.textContent = confirmText;
    }
    if (cancelBtn) {
      cancelBtn.textContent = cancelText;
    }

    // Remove any existing event listeners by cloning buttons
    const newConfirmBtn = confirmBtn.cloneNode(true);
    const newCancelBtn = cancelBtn.cloneNode(true);
    confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);
    cancelBtn.parentNode.replaceChild(newCancelBtn, cancelBtn);

    // Close modal function
    const closeModal = function() {
      modal.classList.remove("is-visible");
      document.body.classList.remove("modal-open");
      
      // Remove overlay click listener
      const overlay = modal.querySelector(".confirmation-modal-overlay");
      if (overlay) {
        overlay.removeEventListener("click", handleOverlayClick);
      }
    };

    // Handle overlay click
    const handleOverlayClick = function() {
      if (onCancel) {
        onCancel();
      }
      closeModal();
    };

    // Setup confirm button
    newConfirmBtn.addEventListener("click", function() {
      if (onConfirm) {
        onConfirm();
      }
      closeModal();
    });

    // Setup cancel button
    newCancelBtn.addEventListener("click", function() {
      if (onCancel) {
        onCancel();
      }
      closeModal();
    });

    // Setup overlay click
    const overlay = modal.querySelector(".confirmation-modal-overlay");
    if (overlay) {
      overlay.addEventListener("click", handleOverlayClick);
    }

    // Show modal
    modal.style.display = "flex";
    document.body.classList.add("modal-open");
    
    // Trigger animation
    setTimeout(function() {
      modal.classList.add("is-visible");
    }, 10);

    // Focus confirm button for accessibility
    setTimeout(function() {
      newCancelBtn.focus();
    }, 100);
  }

  /**
   * Generate header HTML with move buttons, title, and edit button
   * @param {Object} options - Configuration options
   * @param {string} options.itemId - Item ID
   * @param {string} options.displayTitle - Title to display
   * @param {string} options.idAttribute - Data attribute name (e.g., "data-chart-id")
   * @param {string} options.itemType - Item type for CSS classes (e.g., "chart", "faq", "text")
   * @param {string} options.removeButtonClass - CSS class for remove button (e.g., "btn-remove-chart")
   * @param {string} options.removeButtonTitle - Title attribute for remove button
   * @param {string} [options.actionsHtml] - Additional HTML for actions section
   * @returns {string} HTML string for header
   */
  function generateItemHeaderHtml(options) {
    const {
      itemId,
      displayTitle,
      idAttribute,
      itemType,
      removeButtonClass,
      removeButtonTitle,
      actionsHtml = "",
      hideEditTitle = false,
    } = options;

    const escapedTitle = escapeHtml(displayTitle);
    
    // Only show edit title button if not hidden
    const editTitleButton = hideEditTitle ? '' : `
        <button class="btn-edit-title" ${idAttribute}="${itemId}" type="button" title="Edit title">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>`;

    return `
      <div class="${itemType}-item-title">
        <div class="title-order-controls">
          <button class="btn-move-up" ${idAttribute}="${itemId}" type="button" title="Move up">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="19" x2="12" y2="5"></line>
              <polyline points="5 12 12 5 19 12"></polyline>
            </svg>
          </button>
          <button class="btn-move-down" ${idAttribute}="${itemId}" type="button" title="Move down">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <polyline points="19 12 12 19 5 12"></polyline>
            </svg>
          </button>
        </div>
        <h4 class="title-text" ${idAttribute}="${itemId}">${escapedTitle}</h4>${editTitleButton}
      </div>
      <div class="${itemType}-item-actions">
        ${actionsHtml}
        <button class="${removeButtonClass}" ${idAttribute}="${itemId}" title="${escapeHtml(removeButtonTitle)}">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `;
  }

  // Export functions for use in other scripts
  window.ReportBuilderUtils = {
    moveElementUp: moveElementUp,
    moveElementDown: moveElementDown,
    makeTitleEditable: makeTitleEditable,
    makeReportHeaderEditable: makeReportHeaderEditable,
    initReportHeaderEditing: initReportHeaderEditing,
    preserveTinyMCEEditors: preserveTinyMCEEditors,
    restoreTinyMCEEditors: restoreTinyMCEEditors,
    escapeHtml: escapeHtml,
    getCanvasContainer: getCanvasContainer,
    toggleEmptyMessage: toggleEmptyMessage,
    updateArrayOrder: updateArrayOrder,
    setupMoveButtons: setupMoveButtons,
    setupEditTitleButton: setupEditTitleButton,
    generateItemHeaderHtml: generateItemHeaderHtml,
    showConfirmationModal: showConfirmationModal,
  };
})();

